import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, Clock, CheckCircle2, Hammer, Home, Wrench, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const HomePage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const services = [
    {
      icon: Hammer,
      title: 'Builders',
      description: 'Professional building services from brickwork and masonry to carpentry and joinery. Our skilled team handles projects of all sizes.',
      image: 'https://customer-assets.emergentagent.com/job_capital-roofing/artifacts/pvl7in30_Photo%2020-01-2026%2C%2020%2050%2032%20%281%29.jpg'
    },
    {
      icon: Home,
      title: 'House Extensions',
      description: 'Add space and value to your property with our end-to-end extension service. From design to finishing touches.',
      image: 'https://customer-assets.emergentagent.com/job_capital-roofing/artifacts/slmydesr_Photo-20-01-2026%2C-20-50-06.jpg'
    },
    {
      icon: Wrench,
      title: 'Renovations & Restorations',
      description: 'Transform your property with our comprehensive renovation services. We handle large, complex projects with expertise.',
      image: 'https://customer-assets.emergentagent.com/job_capital-roofing/artifacts/r14fb01m_Photo%2020-01-2026%2C%2020%2050%2049.jpg'
    },
    {
      icon: Building2,
      title: 'Roofing Services',
      description: 'Complete roofing solutions including new installations, repairs, and maintenance. Quality workmanship guaranteed.',
      image: 'https://customer-assets.emergentagent.com/job_capital-roofing/artifacts/9yv3qxp8_Photo%2020-01-2026%2C%2020%2051%2054.jpg'
    },
  ];

  const stats = [
    { number: '20+', label: 'Years Experience' },
    { number: 'CITB', label: 'Registered Company' },
    { number: '100%', label: 'Client Satisfaction' },
    { number: 'LE9-CV13', label: 'Service Coverage' },
  ];

  const whyChooseUs = [
    { icon: Award, title: '20+ Years Experience', description: 'Over two decades of exceptional construction expertise' },
    { icon: Users, title: 'CITB-Registered', description: 'Upholding the highest industry standards' },
    { icon: Clock, title: 'Client-Focused', description: 'Friendly, approachable team working closely with you' },
    { icon: CheckCircle2, title: 'Built to Last', description: 'Safe, reliable, and quality-guaranteed projects' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_capital-roofing/artifacts/x89a78a1_Photo%2020-01-2026%2C%2020%2050%2032.jpg"
            alt="Construction"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className={`max-w-3xl transition-all duration-1000 transform ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="text-white">Building Your</span>
              <br />
              <span className="text-[#4ade80]">Dream Property</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              With over 20 years of experience, we're your friendly, trusted construction partner based in Burbage, serving Hinckley, South Leicestershire, Nuneaton and surrounding areas. Quality workmanship, exceptional service.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                size="lg"
                className="bg-[#4ade80] hover:bg-[#22c55e] text-black font-semibold text-lg px-8 py-6"
              >
                <Link to="/contact">
                  Get Free Quote
                  <ArrowRight className="ml-2" size={20} />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-[#4ade80] text-[#4ade80] hover:bg-[#4ade80] hover:text-black text-lg px-8 py-6"
              >
                <Link to="/services">View Services</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#4ade80] rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-[#4ade80] rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#4ade80] mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Welcome to <span className="text-[#4ade80]">Capital Property Improvements</span>
              </h2>
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                With over 20 years of experience, Capital Property Improvements is your friendly, trusted construction partner based in Burbage, serving Hinckley, South Leicestershire, Nuneaton and the Midlands. We specialise in new builds, extensions, and alterations, delivering projects of all sizes with exceptional workmanship and professional expertise.
              </p>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                As a CITB-registered company, we uphold the highest industry standards, ensuring every project is safe, reliable, and built to last. Whether you're planning a dream home, expanding your property, or modernising your space, our approachable team works closely with you to bring your vision to life with care and professionalism.
              </p>
              <Button
                asChild
                size="lg"
                className="bg-[#4ade80] hover:bg-[#22c55e] text-black font-semibold"
              >
                <Link to="/contact">Contact Us Today</Link>
              </Button>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_capital-roofing/artifacts/hyt1j8wq_House-3.jpg"
                alt="Modern Building"
                className="rounded-lg shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#4ade80] rounded-lg -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our <span className="text-[#4ade80]">Services</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Comprehensive building and renovation services tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card
                  key={index}
                  className="bg-[#0a0a0a] border-gray-800 hover:border-[#4ade80] transition-all duration-300 overflow-hidden group hover-lift"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-[#4ade80] rounded-lg flex items-center justify-center">
                        <IconComponent className="text-black" size={24} />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-bold text-white mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 mb-4 leading-relaxed">
                      {service.description}
                    </p>
                    <Button
                      asChild
                      variant="ghost"
                      className="text-[#4ade80] hover:text-[#22c55e] hover:bg-transparent p-0 h-auto"
                    >
                      <Link to="/services" className="inline-flex items-center">
                        Learn More
                        <ArrowRight className="ml-2" size={16} />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button
              asChild
              size="lg"
              className="bg-[#4ade80] hover:bg-[#22c55e] text-black font-semibold"
            >
              <Link to="/services">View All Services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why Choose <span className="text-[#4ade80]">Us</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              At Capital Property Improvements, quality, professionalism, and a friendly, client-focused approach are at the heart of everything we do.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card
                  key={index}
                  className="bg-[#1a1a1a] border-gray-800 hover:border-[#4ade80] transition-all duration-300 text-center hover-lift"
                >
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-[#4ade80]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-[#4ade80]" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-400">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-[#4ade80]/10 to-transparent border-y border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-gray-300 text-lg mb-8 leading-relaxed">
            Get in touch today for a free, no-obligation quote. Our team is ready to bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-[#4ade80] hover:bg-[#22c55e] text-black font-semibold text-lg px-8"
            >
              <Link to="/contact">Get Free Quote</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-black text-lg px-8"
            >
              <a href="tel:07738965968">Call Us Now</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              What Our <span className="text-[#4ade80]">Clients Say</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied customers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-[#0a0a0a] border-gray-800 hover:border-[#4ade80] transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-[#4ade80] text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed italic">
                  "Professional service from start to finish. The team at Capital Property Improvements transformed our home with a stunning extension. Highly recommended!"
                </p>
                <p className="text-[#4ade80] font-semibold">- Hinckley Homeowner</p>
              </CardContent>
            </Card>

            <Card className="bg-[#0a0a0a] border-gray-800 hover:border-[#4ade80] transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-[#4ade80] text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed italic">
                  "Excellent workmanship and attention to detail. Our loft conversion was completed on time and to the highest standard. Very pleased with the results."
                </p>
                <p className="text-[#4ade80] font-semibold">- Burbage Client</p>
              </CardContent>
            </Card>

            <Card className="bg-[#0a0a0a] border-gray-800 hover:border-[#4ade80] transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-[#4ade80] text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed italic">
                  "Friendly, reliable team who went above and beyond. The new roof looks fantastic and the whole process was smooth and stress-free. Thank you!"
                </p>
                <p className="text-[#4ade80] font-semibold">- Nuneaton Customer</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <a
              href="https://g.page/r/YOUR_GOOGLE_REVIEW_LINK/review"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-[#4ade80] hover:text-[#22c55e] font-semibold text-lg"
            >
              Leave us a review on Google
              <ArrowRight className="ml-2" size={20} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;